import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HistoryIcon from '@mui/icons-material/History';
import RedoIcon from '@mui/icons-material/Redo';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import { Box, Button, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type React from 'react';
import type { Section } from '../../../../types/tender';

interface SectionActionRowProps {
  section: Section;
  account: import('../../../../types/auth').User | null;
  canEditSection: boolean;
  isHistoryComparison?: boolean;
  shouldShowDiff: (id: string) => boolean;
  setShowDiffs: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  inlineResolved: Record<string, boolean>;
  chatSectionId: string | null;
  setChatSectionId: (id: string | null) => void;
  editingSectionId: string | null;
  downloadingSectionId: string | null;
  handlers: {
    handleRestoreRevision?: (section: Section) => Promise<void>;
    handleCancelHistoryComparison?: (section: Section) => Promise<void>;
    handleRejectSuggestion: (section: Section) => void;
    handleAcceptSuggestion: (section: Section) => void;
    handleUndo: (section: Section) => Promise<void>;
    handleRedo: (section: Section) => Promise<void>;
    handleOpenAIEdit: (section: Section) => Promise<void>;
    handleCancelEdit: (section: Section) => Promise<void>;
    handleSave: (section: Section) => Promise<boolean>;
    handleOpenManualEdit: (section: Section) => void;
    handleOpenHistory: (section: Section) => Promise<void>;
    handleDownloadSectionDocx: (section: Section) => Promise<void>;
  };
}

const SectionActionRow: React.FC<SectionActionRowProps> = ({
  section,
  account,
  canEditSection,
  isHistoryComparison,
  shouldShowDiff,
  setShowDiffs,
  inlineResolved,
  chatSectionId,
  setChatSectionId,
  editingSectionId,
  downloadingSectionId,
  handlers,
}) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: 1,
        pt: 1,
        borderTop: '1px dashed',
        borderColor: 'divider',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Left Side: Revision Actions */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
        {section.suggested_content &&
          (isHistoryComparison ? (
            // History Mode Actions
            <>
              <Button
                variant="contained"
                color="success"
                size="small"
                startIcon={<RestoreIcon />}
                onClick={() => handlers.handleRestoreRevision?.(section)}
                disabled={!canEditSection || section.status === 'reviewed'}
              >
                Restore
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => handlers.handleCancelHistoryComparison?.(section)}
              >
                Close
              </Button>
            </>
          ) : // Standard AI Suggestion Actions
          (section.content || '').trim() === (section.suggested_content || '').trim() ? (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => handlers.handleRejectSuggestion(section)}
              disabled={!canEditSection}
            >
              Close Review
            </Button>
          ) : shouldShowDiff(section.id) && inlineResolved[section.id] ? (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => handlers.handleAcceptSuggestion(section)}
              disabled={!canEditSection}
            >
              Confirm Resolution
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={() => handlers.handleAcceptSuggestion(section)}
                disabled={!canEditSection}
              >
                Approve All
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => handlers.handleRejectSuggestion(section)}
                disabled={!canEditSection}
              >
                Reject All
              </Button>
            </>
          ))}
        {section.suggested_content && (
          <>
            <Button
              size="small"
              variant="contained"
              onClick={() =>
                setShowDiffs((prev) => ({
                  ...prev,
                  [section.id]: !shouldShowDiff(section.id),
                }))
              }
              sx={{
                color: shouldShowDiff(section.id) ? 'common.white' : 'common.black',
                bgcolor: shouldShowDiff(section.id)
                  ? theme.palette.bidlogiq.brick
                  : theme.palette.grey[300],
                '&:hover': {
                  bgcolor: shouldShowDiff(section.id)
                    ? theme.palette.bidlogiq.orange
                    : theme.palette.grey[400],
                },
                fontWeight: 'bold',
                textTransform: 'none',
              }}
            >
              {shouldShowDiff(section.id) ? 'Hide Changes' : 'Show Changes'}
            </Button>

            {/* Inline Diff Undo/Redo Controls */}
            {shouldShowDiff(section.id) && (
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Undo last change">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UndoIcon />}
                      onClick={() => handlers.handleUndo(section)}
                      disabled={!canEditSection || section.status === 'reviewed'}
                      sx={{
                        color: 'text.primary',
                        borderColor: 'divider',
                        minWidth: 0,
                        px: 1,
                        fontWeight: 'bold',
                      }}
                    >
                      Undo
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Redo last change">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RedoIcon />}
                      onClick={() => handlers.handleRedo(section)}
                      disabled={!canEditSection || section.status === 'reviewed'}
                      sx={{
                        color: 'text.primary',
                        borderColor: 'divider',
                        minWidth: 0,
                        px: 1,
                        fontWeight: 'bold',
                      }}
                    >
                      Redo
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Right Side: Edit Actions */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Tooltip title="Undo last change">
          <span>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handlers.handleUndo(section);
              }}
              disabled={!canEditSection || section.status === 'reviewed'}
              sx={{ color: 'text.secondary' }}
            >
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          title={
            section.lockedBy && section.lockedBy.uid !== account?.uid
              ? `Locked by ${section.lockedBy.displayName}`
              : !canEditSection
                ? 'No edit permission'
                : section.status === 'reviewed'
                  ? 'Section is reviewed'
                  : ''
          }
        >
          <span>
            <Button
              variant="contained"
              size="small"
              startIcon={<AutoAwesomeIcon />}
              onClick={() =>
                chatSectionId === section.id
                  ? setChatSectionId(null)
                  : handlers.handleOpenAIEdit(section)
              }
              disabled={
                !!(section.lockedBy && section.lockedBy.uid !== account?.uid) ||
                !canEditSection ||
                section.status === 'reviewed'
              }
              sx={{
                bgcolor: theme.palette.bidlogiq.brick,
                color: 'white',
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                minWidth: '90px',
                '&:hover': { bgcolor: theme.palette.bidlogiq.rust },
                '&.Mui-disabled': {
                  bgcolor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              {!section.content || section.content.trim() === '' ? 'Create with AI' : 'AI Edit'}
            </Button>
          </span>
        </Tooltip>
        {editingSectionId === section.id ? (
          <>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={() => handlers.handleCancelEdit(section)}
              sx={{ fontWeight: 'bold', minWidth: '80px' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={() => handlers.handleSave(section)}
              sx={{ fontWeight: 'bold', minWidth: '90px' }}
            >
              Save
            </Button>
          </>
        ) : (
          <Tooltip
            title={
              section.suggested_content
                ? 'Resolve pending suggestion first.'
                : section.lockedBy && section.lockedBy.uid !== account?.uid
                  ? `Locked by ${section.lockedBy.displayName}`
                  : !canEditSection
                    ? 'No edit permission'
                    : section.status === 'reviewed'
                      ? 'Section is reviewed'
                      : ''
            }
          >
            <span>
              <Button
                size="small"
                startIcon={<EditIcon />}
                onClick={() => handlers.handleOpenManualEdit(section)}
                disabled={
                  !canEditSection ||
                  chatSectionId === section.id ||
                  !!section.suggested_content ||
                  !!(section.lockedBy && section.lockedBy.uid !== account?.uid) ||
                  section.status === 'reviewed'
                }
                sx={{
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  bgcolor: theme.palette.greyLight,
                  color: 'text.primary',
                  minWidth: '90px',
                  '&:hover': { bgcolor: theme.palette.grey[400] },
                }}
              >
                Edit
              </Button>
            </span>
          </Tooltip>
        )}

        <Tooltip title="View Version History">
          <IconButton
            size="small"
            onClick={() => handlers.handleOpenHistory(section)}
            sx={{
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>

        <Tooltip
          title={
            section.lockedBy && section.lockedBy.uid !== account?.uid
              ? `Locked by ${section.lockedBy.displayName}`
              : ''
          }
        >
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                downloadingSectionId === section.id ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileDownloadIcon />
                )
              }
              onClick={() => handlers.handleDownloadSectionDocx(section)}
              disabled={
                !!downloadingSectionId ||
                !!(section.lockedBy && section.lockedBy.uid !== account?.uid)
              }
              sx={{
                fontWeight: 'bold',
                whiteSpace: 'nowrap',
                minWidth: 'auto',
                borderColor: theme.palette.grey[400],
                color: theme.palette.text.primary,
                '&:hover': {
                  borderColor: theme.palette.text.primary,
                  bgcolor: theme.palette.grey[100],
                },
              }}
            >
              DOCX
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default SectionActionRow;
